// src/views/activity-log/ActivityLogRow.jsx
// Phase 40 Plan 04 Task 2 — list row. AU-04. UI-SPEC #9. RESEARCH.md Pattern 10.
//
// Renders a single activity_log entry: timestamp / actor + verb + target /
// optional payload summary. Verb mapping is colocated here so the renderer
// stays a pure function of the entry shape.

import React from 'react';

function formatTs(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return '';
  }
}

// Map an activity_log action + payload to a UK-English description string.
// Returns { verb, target, summary?: string }.
export function describeActivity(entry, currentUserId) {
  const action = entry?.action || '';
  const payload = entry?.payload || {};
  const targetType = entry?.target_type || '';
  const isSelf = currentUserId && entry?.actor_user_id === currentUserId;
  const actor = isSelf ? 'You' : (entry?.actor_email || 'Another member');

  // Resolve a human-readable name for the target.
  const name = payload.name || payload.plant_name || payload.area_name || entry?.target_id || '';

  // Default fallback maps `<table>.<verb>` → "<verbed> <table>".
  let verb = '';
  let targetLabel = '';
  let summary = null;

  switch (action) {
    case 'plants.created':
    case 'plant.created':
      verb = 'added plant';
      targetLabel = name;
      break;
    case 'plants.updated':
    case 'plant.updated':
      verb = 'edited plant';
      targetLabel = name;
      break;
    case 'plants.deleted':
    case 'plant.deleted':
      verb = 'removed plant';
      targetLabel = name;
      break;
    case 'task.completed':
    case 'completions.created':
      verb = 'completed';
      targetLabel = `${payload.task_type || 'task'} for ${payload.plant_name || ''}`.trim();
      break;
    case 'task.dismissed':
    case 'dismissals.created':
      verb = 'dismissed';
      targetLabel = `${payload.task_type || 'task'} for ${payload.plant_name || ''}`.trim();
      break;
    case 'areas.created':
    case 'area.created':
      verb = 'added area';
      targetLabel = name;
      break;
    case 'areas.updated':
    case 'area.updated':
      verb = 'edited area';
      targetLabel = name;
      break;
    case 'areas.deleted':
    case 'area.deleted':
      verb = 'removed area';
      targetLabel = name;
      break;
    case 'garden.uploaded': {
      verb = 'uploaded garden —';
      const c = payload.row_counts || {};
      const plantCount = c.plants ?? 0;
      const areaCount = c.areas ?? 0;
      targetLabel = `${plantCount} plants, ${areaCount} areas`;
      break;
    }
    case 'share.granted':
      verb = 'shared with';
      targetLabel = payload.invitee_email || 'a partner';
      break;
    case 'tier.changed':
      verb = 'upgraded to';
      targetLabel = payload.to_tier || 'premium';
      break;
    default:
      verb = 'performed';
      targetLabel = action || targetType || '';
      break;
  }

  return { actor, verb, targetLabel, summary };
}

export default function ActivityLogRow({ entry, currentUserId = null }) {
  const { actor, verb, targetLabel } = describeActivity(entry, currentUserId);
  const ts = formatTs(entry?.created_at);

  return (
    <div style={{
      background: 'var(--c-bg)',
      borderBottom: '1px solid var(--c-border)',
      padding: 16,
    }}>
      <small className="t-meta" style={{ color: 'var(--c-text-3)', fontSize: 'var(--font-tiny)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        <em>{ts}</em>
      </small>
      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-body-size)', color: 'var(--c-text)', lineHeight: 1.55 }}>
        <em>{actor}</em> {verb}{targetLabel ? <> <em>{targetLabel}</em></> : null}
      </p>
    </div>
  );
}
